import { Link, useNavigate } from '@tanstack/react-router';
import type { CrmBoardQuery, CrmBoardSegment, CrmBoardSegmentCounts, CrmCityDto } from '@upme/contracts';
import { Badge } from '@upme/ui/components/badge';
import { Button } from '@upme/ui/components/button';
import { Label } from '@upme/ui/components/label';
import { Popover, PopoverContent, PopoverTrigger } from '@upme/ui/components/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@upme/ui/components/select';
import { cn } from '@upme/ui/lib/utils';
import { type ReactNode, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CRM_PARTNERSHIP_STATUS_LABEL_RU, CRM_PARTNERSHIP_STATUS_ORDER } from '../lib/partnership-status';
import { CRM_SEGMENT_LABEL_RU, CRM_SEGMENT_ORDER } from '../lib/segments';
import { normalizeCrmBoardQuery } from '../queries';

const ANY = '__any__';

type Props = {
  filters: CrmBoardQuery;
  counts?: CrmBoardSegmentCounts;
  cities: ReadonlyArray<CrmCityDto>;
  eventsMode?: boolean;
};

function crmSearchFromFilters(filters: CrmBoardQuery): Record<string, string> {
  const n = normalizeCrmBoardQuery(filters);
  const out: Record<string, string> = { segment: n.segment };
  if (n.cityId) out.cityId = n.cityId;
  if (n.partnershipStatus) out.partnershipStatus = n.partnershipStatus;
  return out;
}

function HeaderCity({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    setTarget(document.getElementById('crm-header-controls'));
  }, []);
  return target ? createPortal(children, target) : children;
}

export function CrmBoardToolbar({ filters, counts, cities, eventsMode = false }: Props) {
  const navigate = useNavigate();
  const segment = filters.segment ?? 'all';

  const patchFilters = (next: CrmBoardQuery) => {
    if (eventsMode) {
      void navigate({ to: '/crm/events', search: next.cityId ? { cityId: next.cityId } : {} });
      return;
    }
    void navigate({ to: '/crm', search: () => crmSearchFromFilters(normalizeCrmBoardQuery(next)) });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <nav
        aria-label="Сегменты"
        className="flex min-w-0 flex-1 flex-row flex-nowrap gap-1 overflow-x-auto border-b border-border/70"
      >
        {CRM_SEGMENT_ORDER.map((key) => (
          <Link
            key={key}
            to="/crm"
            search={() => crmSearchFromFilters({ ...filters, segment: key as CrmBoardSegment })}
            aria-current={!eventsMode && key === segment ? 'page' : undefined}
            className={cn(
              'inline-flex shrink-0 items-center gap-2 whitespace-nowrap px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              !eventsMode && key === segment
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {CRM_SEGMENT_LABEL_RU[key]}
            {counts ? (
              <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-xs">
                {counts[key]}
              </Badge>
            ) : null}
          </Link>
        ))}
        <Link
          to="/crm/events"
          search={filters.cityId ? { cityId: filters.cityId } : {}}
          aria-current={eventsMode ? 'page' : undefined}
          className={cn(
            'inline-flex shrink-0 items-center whitespace-nowrap px-3 py-2 text-sm font-medium',
            eventsMode
              ? 'border-b-2 border-primary text-primary'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Ивенты
        </Link>
      </nav>

      <div className="flex items-center gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button type="button" variant="outline" className={cn(eventsMode && 'hidden')}>
              Фильтры{filters.partnershipStatus ? ' · 1' : ''}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-3">
            <Label className="text-xs">Статус сотрудничества</Label>
            <Select
              value={filters.partnershipStatus ?? ANY}
              onValueChange={(v) => {
                const base: CrmBoardQuery = { segment: filters.segment ?? 'all' };
                if (filters.cityId) base.cityId = filters.cityId;
                if (v === ANY) {
                  patchFilters(base);
                  return;
                }
                patchFilters({
                  ...base,
                  partnershipStatus: v as NonNullable<CrmBoardQuery['partnershipStatus']>,
                });
              }}
            >
              <SelectTrigger aria-label="Статус сотрудничества">
                <SelectValue placeholder="Все" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Все</SelectItem>
                {CRM_PARTNERSHIP_STATUS_ORDER.map((status) => (
                  <SelectItem key={status} value={status}>
                    {CRM_PARTNERSHIP_STATUS_LABEL_RU[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PopoverContent>
        </Popover>

        <HeaderCity>
          <div className="w-40 max-w-full sm:w-48">
            <Select
              value={filters.cityId ?? ANY}
              onValueChange={(v) => {
                const base: CrmBoardQuery = { segment: filters.segment ?? 'all' };
                if (filters.partnershipStatus) base.partnershipStatus = filters.partnershipStatus;
                if (v === ANY) {
                  patchFilters(base);
                  return;
                }
                patchFilters({ ...base, cityId: v });
              }}
            >
              <SelectTrigger aria-label="Город">
                <SelectValue placeholder="Все города" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Все города</SelectItem>
                {cities.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </HeaderCity>
      </div>
    </div>
  );
}
